<template>
  <b-row style="position:relative">
    <b-col cols="12" ref="tokens">
      <div class="filters-container">
        <div class="d-flex flex-row">
          <button class="btn refresh btn-sm" @click.prevent="addNewToken">{{$t('Add token')}}</button>
        </div>
        <DropdownColumns v-model="fields" class="customize" />
      </div>
      <b-table
        id="tokens-table"
        :busy.sync="isBusy"
        show-empty
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :hover="true"
        :striped="true"
        stacked="md"
        :items="provider"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        @row-clicked="rowClicked"
        tbody-tr-class="tr-pointer"
        v-columns-resizable="{afterResize: columnsWidthChanged}"
      >
        <template v-slot:empty="scope">
          <div class="text-center">{{ $t(scope.emptyText) }}</div>
        </template>

        <template v-slot:head(id)="data">{{ $t(data.label) }}</template>
        <template v-slot:head(name)="data">{{ $t(data.label) }}</template>
        <template v-slot:head(active)="data">{{ $t(data.label) }}</template>
        <template v-slot:head(value)="data">{{ $t(data.label) }}</template>
        <template v-slot:head(permissions)="data">{{ $t(data.label) }}</template>
        <template v-slot:head(valid_until)="data">{{ $t(data.label) }}</template>
        <template v-slot:head(allowed_ips)="data">{{ $t(data.label) }}</template>
        <template v-slot:head(secret_key)="data">{{ $t(data.label) }}</template>

        <template v-slot:cell(permissions)="data">
          <span>{{data.item.permissions.length}} {{ $t("of") }} {{permissionsList.length}}</span>
        </template>

        <template v-slot:cell(active)="data">
          <span v-if="data.item.active">{{ $t("Activated") }}</span>
          <span v-if="!data.item.active">{{ $t("Deactivated") }}</span>
        </template>

        <template v-slot:cell(valid_until)="data">
          <span v-if="data.item.valid_until === 'never expires'">{{ $t("Does not expire") }}</span>
          <span v-if="data.item.valid_until !== 'never expires'">{{ data.item.valid_until }}</span>
        </template>

        <template v-slot:cell(allowed_ips)="data">
          <span v-if="data.item.allowed_ips === 'all'">{{ $t("any IP") }}</span>
          <span v-if="data.item.allowed_ips !== 'all'">{{ data.item.allowed_ips }}</span>
        </template>
      </b-table>
      <div class="container-fluid">
        <div class="row">
          <div style="white-space:nowrap;" class="my-2">
            <PaginationSelect
              v-model="perPage"
              :options="paginationOpts"
              variant="brand"
              size="sm"
            />
            <span>
              &nbsp;
              <i18n path="Showing {start}-{end} of {total} rows" tag="span">
                <span slot="start">{{(currentPage-1) * perPage + 1}}</span>
                <span slot="end">{{currentPage * perPage}}</span>
                <span slot="total">{{totalRows}}</span>
              </i18n>
            </span>
            <b-button
              size="sm"
              @click.stop="$root.$emit('bv::refresh::table', 'tokens-table')"
              class="ml-2 refresh"
              variant="light"
              :title="$t('Refresh')"
            >
              <i class="icon-refresh"></i>
            </b-button>
          </div>
          <nav class="ml-auto mt-2">
            <b-pagination
              size="md"
              :total-rows="totalRows"
              :per-page="perPage"
              v-model="currentPage"
            />
          </nav>
        </div>
      </div>
    </b-col>
    <b-modal id="bv-modal-token" size="xl" hide-footer centered>
      <template slot="modal-title">{{addMode ? $t('New Token') : $t('Token Details')}}</template>
      <template slot="modal-header-close">
        <span />
      </template>

      <FloatingLabel
        v-model="details.value"
        label="Encrypted token"
        size="sm"
        v-if="!addMode && details.value"
        :readonly="true"
      />

      <div v-if="!addMode" style="text-align: center;">
        <b-button
          type="button"
          :variant="details.active ? 'secondary' : 'primary'"
          :disabled="details.active || !details.id"
          @click.prevent="doActivateToken"
        >{{$t('Activate')}}</b-button>
        <b-button
          type="button"
          :variant="details.active ? 'primary' : 'secondary'"
          :disabled="!details.active || !details.id"
          @click.prevent="doDeactivateToken"
        >{{$t('Deactivate')}}</b-button>&nbsp;
        <b-button
          type="button"
          variant="danger"
          :disabled="!details.id"
          @click.prevent="doDeleteToken"
        >{{$t('Delete')}}</b-button>
      </div>

      <div class="d-block" style="position:relative">
        <b-form @submit.prevent="doAddToken" autocomplete="off" ref="token">
          <FloatingLabel
            v-model="details.name"
            label="Name"
            size="sm"
            :readonly="!addMode"
            :state="addMode ? details.name ? true : false : null"
          />

          <FloatingLabel
            v-model="details.allowed_ips"
            label="Allowed IPs"
            :placeholder="$t('any IP')"
            size="sm"
            :readonly="!addMode"
            :state="addMode ? checkAllowedIPs(details.allowed_ips) ? true : false : null"
          />

          <FloatingLabel
            v-if="!addMode"
            v-model="details.valid_until"
            label="Valid until"
            :placeholder="$t('forever')"
            size="sm"
            :readonly="!addMode"
          />
          <b-form-group
            v-if="addMode"
            :label="$t('Valid until')"
            class="grouping py-2 px-3 mb-3 mt-0"
          >
            <b-form-checkbox
              v-model="validForever"
              :value="true"
              :unchecked-value="false"
            >{{$t('Does not expire')}}</b-form-checkbox>
            <DateRangePicker
              v-if="!validForever"
              @update="updateDate"
              class="custom-picker"
              :singleDatePicker="true"
              :timePicker="true"
              :show-dropdown="true"
              :reset="true"
              opens="left"
            />
          </b-form-group>

          <FloatingLabel
            v-model="details.secret_key"
            label="Secret key"
            :placeholder="$t('none')"
            size="sm"
            :readonly="!addMode"
          />

          <div class="mb-2 position-relative">
            <div class="multiselect-lbl" v-if="details.permissions">{{$t('Token permissions')}}</div>
            <multiselect
              v-model="details.permissions"
              :options="permissionsList"
              placeholder="Select permissions (leave empty to add all)"
              track-by="name"
              :label="addMode ? 'name' : 'description'"
              tooltip="description"
              :multiple="true"
              :hideSelected="true"
              :disabled="!addMode"
              :closeOnSelect="true"
              openDirection="below"
            ></multiselect>
          </div>

          <div style="text-align: right;" v-if="addMode">
            <b-button type="submit" variant="success" :disabled="!validNewTokenData">{{$t('Save')}}</b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
  </b-row>
</template>

<script>
import startCase from "@/shared/strings";
import { errorConf, successConf } from "@/shared/notifications";
import PaginationSelect from "@/components/PaginationSelect";
import tableMixin from "@/shared/mixins/table.mixin";
import DropdownColumns from "@/components/DropdownColumns";
import FloatingLabel from "@/components/FloatingLabel";
import Multiselect from "@/components/multiselect-tooltip"; // NOTE: forked/new variant
import DateRangePicker from "@/components/calendar-range/DateRangePicker";
import moment from "moment";

export default {
  components: {
    PaginationSelect,
    DropdownColumns,
    FloatingLabel,
    Multiselect,
    DateRangePicker
  },
  name: "OwnTokens",
  //props: ["euser_id"],
  mixins: [tableMixin],
  data: function() {
    return {
      tableName: "tokens",

      isBusy: false,
      sortBy: "name",
      sortDesc: false,
      userPermissions: [],
      validForever: true,

      ranges: {
        Today: [moment().endOf("day")],
        Tomorrow: [
          moment()
            .add(1, "days")
            .endOf("day")
        ],
        "This week": [moment().endOf("isoWeek")],
        "Next week": [
          moment()
            .add(1, "weeks")
            .endOf("isoWeek")
        ],
        "This month": [moment().endOf("isoMonth")],
        "Next month": [
          moment()
            .add(1, "months")
            .endOf("isoMonth")
        ],
        "This year": [moment().endOf("year")],
        "Next year": [
          moment()
            .add(1, "year")
            .endOf("year")
        ]
      },

      fields: [
        { key: "id", sortable: true, class: "", ignore: true },
        { key: "name", sortable: true, class: "", ignore: true },
        { key: "active", sortable: true, class: "", ignore: true },
        {
          key: "value",
          label: "Encrypted token",
          sortable: false,
          class: "d-none",
          ignore: true
        },
        { key: "permissions", sortable: true, class: "", ignore: true },
        { key: "valid_until", sortable: true, class: "", ignore: true },
        { key: "allowed_ips", sortable: true, class: "", ignore: true },
        { key: "secret_key", sortable: true, class: "", ignore: true }
      ],

      currentPage: 1,
      perPage: 10,
      totalRows: 0,

      details: {
        id: null,
        name: "",
        active: false,
        value: null,
        permissions: null,
        valid_until: null,
        allowed_ips: null,
        secret_key: null
      },

      addMode: true
    };
  },

  watch: {},

  computed: {
    allPermissionsList() {
      return this.$store.getters["cache/permissions"];
    },

    permissionsList() {
      let ups = new Set();
      this.userPermissions.forEach(element => {
        let es = new Set(element.actions);
        ups = new Set([...ups, ...es]);
      });
      let pl = [];
      this.allPermissionsList.forEach(element => {
        if (ups.has(element.name)) pl.push({ ...element });
      });
      return pl;
    },

    validNewTokenData() {
      if (this.checkAllowedIPs(this.details.allowed_ips) || this.details.name)
        return true;
      return false;
    }
  },

  mounted() {
    /*
    console.log("DEBUG (mount) - this.euser_id:", this.eid); // debug only
    if (this.eid != "0" && this.eid != "") {
      this.$root.$emit("bv::refresh::table", "tokens-table");
    }
    */
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      // console.log("modalId", modalId);
      if (modalId == "bv-modal-token") {
        this.details.id = null;
        this.details.name = "";
        this.details.active = false;
        this.details.value = null;
        this.details.permissions = null;
        this.details.valid_until = null;
        this.details.allowed_ips = null;
        this.details.secret_key = null;
        this.addMode = true;
        this.validForever = true;
      }
    });

    const loader = this.$loading.open({
      container: this.$refs.cnt
    });

    Promise.all([
      this.$store.dispatch("cache/fetch", "permissions").catch(e => e),
      this.$store
        .dispatch("auth/mePermissions")
        .then(resp => (this.userPermissions = resp.data.roles))
        .catch(e => e)
    ]).finally(() => {
      console.log("DEBUG - userPermissions:", this.userPermissions);
      loader.close();
    });
  },

  destroyed() {
    this.$root.$off("bv::modal::hide");
  },

  methods: {
    refresh() {
      this.$root.$emit("bv::refresh::table", "tokens-table");
    },

    doAddToken() {
      const loader = this.$loading.open({
        container: this.$refs.token
      });

      let apiAction = "token/create";
      const detailsData = {};

      // paranoia mode checks (should be normally redundant)
      if (!this.addMode) {
        loader.close();
        return;
      }
      if (this.details.name && this.details.name != "") {
        detailsData.name = this.details.name;
      } else {
        this.showErrorNotification("A token name is required");
        loader.close();
        return;
      }

      // should we maybe force having a secret key???
      if (this.details.secret_key)
        detailsData.secret_key = this.details.secret_key;

      if (!this.validForever && this.details.valid_until)
        detailsData.valid_until = this.details.valid_until;

      // see and also update method checkAllowedIPs if planning to change valid separators
      if (this.details.allowed_ips)
        detailsData.allowed_ips = this.details.allowed_ips.split(/[\s,;:]+/);

      // populate detailsData.permissions from details.permissions
      if (this.details.permissions && this.details.permissions.length > 0) {
        let temp = [];
        this.details.permissions.forEach(element => temp.push(element.name));
        detailsData.permissions = temp;
      }

      this.$store
        .dispatch(apiAction, detailsData)
        .then(resp => {
          let msg = "Unexpected response on token creation";
          if (this.addMode && resp.data.id) {
            msg = `Token (id:${resp.data.id}) created and set as active`;
          }
          this.showSuccessNotification(msg);
          this.$root.$emit("bv::refresh::table", "tokens-table");
          this.$bvModal.hide("bv-modal-token");
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    },

    doActivateToken() {
      const loader = this.$loading.open({
        container: this.$refs.token
      });

      let apiAction = "token/activate";
      if (this.addMode || !this.details.id) {
        // paranoia mode check
        loader.close();
        return;
      }

      this.$store
        .dispatch(apiAction, this.details.id)
        .then(resp => {
          let msg = "Unexpected response on token activation";
          if (!this.addMode && resp.data.activated === true) {
            msg = `Token activated`;
          }
          this.showSuccessNotification(msg);
          this.$root.$emit("bv::refresh::table", "tokens-table");
          this.$bvModal.hide("bv-modal-token");
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    },

    doDeactivateToken() {
      const loader = this.$loading.open({
        container: this.$refs.token
      });

      let apiAction = "token/deactivate";
      if (this.addMode || !this.details.id) {
        // paranoia mode check
        loader.close();
        return;
      }

      this.$store
        .dispatch(apiAction, this.details.id)
        .then(resp => {
          let msg = "Unexpected response on token deactivation";
          if (!this.addMode && resp.data.activated === false) {
            msg = `Token deactivated`;
          }
          this.showSuccessNotification(msg);
          this.$root.$emit("bv::refresh::table", "tokens-table");
          this.$bvModal.hide("bv-modal-token");
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    },

    doDeleteToken() {
      const loader = this.$loading.open({
        container: this.$refs.token
      });

      let apiAction = "token/delete";
      if (this.addMode || !this.details.id) {
        // paranoia mode check
        loader.close();
        return;
      }

      this.$store
        .dispatch(apiAction, this.details.id)
        .then(resp => {
          let msg = "Unexpected response on token deletion";
          if (
            !this.addMode &&
            resp.data.deleted &&
            resp.data.deleted.length > 0
          ) {
            msg = `Token (id:${resp.data.deleted[0]}) deleted`;
          }
          this.showSuccessNotification(msg);
          this.$root.$emit("bv::refresh::table", "tokens-table");
          this.$bvModal.hide("bv-modal-token");
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    },

    rowClicked(item, index, event) {
      if (item.id) {
        this.$bvModal.show("bv-modal-token");
        this.$nextTick(() => {
          this.addMode = false;

          const loader = this.$loading.open({
            container: this.$refs.token
          });

          this.details.id = item.id ? item.id : null;
          this.details.name = item.name ? item.name : "";
          this.details.active = item.active ? true : false;
          this.details.value = item.value ? item.value : null;
          this.details.valid_until = item.valid_until ? item.valid_until : null;
          this.details.allowed_ips = item.allowed_ips ? item.allowed_ips : null;
          this.details.secret_key = item.secret_key ? item.secret_key : null;

          // populate details.permissions from allPermissionsList by clicked row permissions
          let temp1 = new Set(item.permissions);
          let temp2 = [];
          this.allPermissionsList.forEach(element => {
            if (temp1.has(element.name)) temp2.push({ ...element });
          });
          this.details.permissions = temp2;
          loader.close();
          console.log("DEBUG - load modal details:", this.details); // debug only
        });
      }
    },

    addNewToken() {
      /*
      if (!this.eid) {
        return;
      }
      */
      this.$bvModal.show("bv-modal-token");
      this.$nextTick(() => {
        this.addMode = true;
        const loader = this.$loading.open({
          container: this.$refs.token
        });
        this.details.id = null;
        this.details.name = "";
        this.details.active = false;
        this.details.value = null;
        this.details.permissions = null;
        this.details.valid_until = null;
        this.details.allowed_ips = null;
        this.details.secret_key = null;
        loader.close();
      });
    },

    provider(ctx, callback) {
      /*
      if (!this.eid) {
        callback([]);
        return;
      }
      */

      const loading = this.$loading.open({
        container: this.$refs.tokens
      });

      this.$store
        .dispatch("token/fetch")
        .then(resp => {
          // console.log("DEBUG (provider) - resp.data:", resp.data); // debug only
          if (resp.success) {
            this.totalRows = resp.data.tokens.length
              ? resp.data.tokens.length
              : 0;
            resp.data.tokens.forEach((item, index) => {
              resp.data.tokens[index]["_cellVariants"] = {};
              if (item.active) {
                resp.data.tokens[index]["_cellVariants"]["active"] = "success";
              } else {
                resp.data.tokens[index]["_cellVariants"]["active"] = "danger";
              }
            });
            callback(resp.data.tokens);
          }
        })
        .catch(err => {
          this.$bvToast.toast(
            this.$t(err.message),
            errorConf(this.$t("Error"))
          );
          this.totalRows = 0;
          callback([]);
        })
        .finally(() => {
          loading.close();
        });

      // Must return null or undefined to signal b-table that callback is being used
      return null;
    },

    showErrorNotification(err) {
      if (err.details && err.details.length) {
        this.$bvToast.toast(
          this.$t(err.details[0]),
          errorConf(this.$t("Error"))
        );
      } else {
        this.$bvToast.toast(this.$t(err.message), errorConf(this.$t("Error")));
      }
    },

    showSuccessNotification(msg) {
      this.$bvToast.toast(this.$t(msg), successConf(this.$t("Success")));
    },

    updateDate(val) {
      if (typeof val.startDate != "undefined")
        this.details.valid_until = val.startDate;
    },

    checkAllowedIPs(txt) {
      let chk = true;
      // leaving IPs field blank means allow all IPs and is a valid option
      if (txt !== "" && txt !== null) {
        // allow any amount of multiple separators between IPs (spaces, commas, colons, semicolons)
        let temp = txt.split(/[\s,;:]+/);
        // if any non-empty chunk fails IP validity checks, fail entire check
        temp.forEach(ip => {
          if (ip && this.validateSingleIP(ip) === false) chk = false;
        });
      }
      return chk;
    },

    validateSingleIP(txt) {
      const ipformat = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      if (txt.match(ipformat)) return true;
      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
</style>


